/* eslint-disable @typescript-eslint/no-unused-vars */
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import { formatNumberWithSuffix } from 'utils/FormatNumber';
import {
  TelegramChannelsRowInfo,
  TelegramChannelsTableSortKeys,
} from 'api/types/httpsTypes/contracts';
import { twMerge } from 'tailwind-merge';
import Big, { BigSource } from 'big.js';
import { MizarHexagonImage } from 'modules/shared-components/hex-logo';
import { getAssetsUrl } from 'utils/urlUtils';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Tooltip from 'modules/shared-components/tooltip';
import { AlertRiskyChannel } from '../../../../components/alerts/riskyChannelAlert';
import { Tabs } from '../../../../components/layout/SIDEBAR_CONST';

export const desktopColumns: TableColumn<TelegramChannelsRowInfo>[] = [
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The Telegram channel or group scraped.">
          <div>Channels</div>
        </Tooltip>
      </div>
    ),

    key: 'CHANNEL',
    component: ({ data }) => (
      <div className="xxs:flex items-center xxs:gap-1">
        <MizarHexagonImage
          size={40}
          alt="Trader logo"
          src={`${getAssetsUrl()}/static/img/telegram/${data.id}.png`}
        />

        <div className="space-y-1">
          <div className="flex space-x-1 items-center">
            <div className="">{AlertRiskyChannel(data)}</div>
            <div className="max-w-[160px] md:max-w-[130px]">
              <Link
                to={`/dex/research/${Tabs.TelegramChannels}/${data.id}/details`}
                className="xxs:font-semibold xxs:text-dex-white text-sm"
              >
                <div className="truncate text-xs">
                  {data.telegramTradingChannelName}
                </div>
              </Link>
            </div>
          </div>
          <a
            href={data.telegramTradingChannelLink}
            className="truncate my-0 xxs:text-dex-white-secondary hover:text-dex-white text-xs"
          >
            @{data.telegramTradingChannelLink?.replace('https://t.me/', '')}
          </a>
        </div>
      </div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The highest all-time high returns of the token being called in the selected period.">
          <div>Highest Returns</div>
        </Tooltip>
      </div>
    ),
    key: 'RETURNS',
    sortableKey: TelegramChannelsTableSortKeys.HighestWins,
    component: ({ data }) => (
      <div
        className={`xxs:text-xs ${
          !data.maxHighestReturn
            ? ''
            : Big(data.maxHighestReturn ?? 0).gte(0)
            ? 'xxs:text-green-500'
            : 'xxs:text-red-500'
        }`}
      >
        <>
          <PercentageChangeWithMaxValue
            className="font-bold"
            change={data.maxHighestReturn || 0}
            maxValue={999}
          />
        </>
      </div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The total number of calls in the selected period.">
          <div>Calls</div>
        </Tooltip>
      </div>
    ),
    key: 'CALLS',
    sortableKey: TelegramChannelsTableSortKeys.Calls,
    component: ({ data }) => (
      <div className="text-xs">{data.totalNumberOfCalls ?? '--'}</div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The count of times where hits achieved an all-time high returns greater than 25%, and did not perform a rug-pull.">
          <div>Wins</div>
        </Tooltip>
      </div>
    ),
    key: 'WINS',
    sortableKey: TelegramChannelsTableSortKeys.Wins,
    component: ({ data }) => (
      <div className="text-xs">{data.numberOfWins ?? '--'}</div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The count of times where hits did not achieve an all-time high returns greater than 25%, and did not perform a rug-pull..">
          <div>Losses</div>
        </Tooltip>
      </div>
    ),
    sortableKey: TelegramChannelsTableSortKeys.Loss,
    key: 'LOSSES',
    component: ({ data }) => (
      <div className="text-xs">{data.numberOfLosses ?? '--'}</div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The count of times where hits performed a rug-pull, after being called.">
          <div>Rugs</div>
        </Tooltip>
      </div>
    ),
    sortableKey: TelegramChannelsTableSortKeys.RugPulls,
    key: 'RUGS',
    component: ({ data }) => (
      <div className="text-xs">
        <span
          className={classNames(
            'font-bold',
            !data.rugPullRatio
              ? ''
              : Big(data.rugPullRatio).gt(0.05)
              ? 'text-red-500'
              : ''
          )}
        >
          {data.numberOfRugPulls ?? '--'}
        </span>
      </div>
    ),
  },
  {
    label: (
      <div className="xxs:text-dex-white-secondary hover:text-dex-white">
        <Tooltip text="The number of wins compared to the total number of calls.">
          <div>Hit Ratio</div>
        </Tooltip>
      </div>
    ),
    sortableKey: TelegramChannelsTableSortKeys.WinsVsLoss,
    key: 'HIT_RATIO',
    component: ({ data }) => (
      <div className="text-xs">
        {Big(data.hitRate ?? 0)
          .mul(100)
          .toFixed(1) ?? '--'}
        %
      </div>
    ),
  },
];

export const mobileColumns: TableColumn<TelegramChannelsRowInfo>[] = [
  {
    key: 'CHANNEL',
    component: ({ data }) => (
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <div className="xxs:flex items-center xxs:gap-1">
            <MizarHexagonImage
              size={50}
              alt="Trader logo"
              src={`${getAssetsUrl()}/static/img/telegram/${data.id}.png`}
            />

            <div className="space-y-1">
              <div className="flex space-x-1 items-center ">
                <div className="">{AlertRiskyChannel(data)}</div>
                <div className="max-w-[160px] md:max-w-[130px]">
                  <Link
                    to={`/dex/research/${Tabs.TelegramChannels}/${data.id}/details`}
                    className="xxs:font-semibold text-white text-base"
                  >
                    <div className="truncate">
                      {data.telegramTradingChannelName}
                    </div>
                  </Link>
                </div>
              </div>
              <a
                href={data.telegramTradingChannelLink}
                className="truncate my-0 text-black-200 hover:text-black-100"
              >
                @{data.telegramTradingChannelLink?.replace('https://t.me/', '')}
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end justify-end">
          <div className="flex space-x-1 items-center ">
            <div>
              <div
                className={`xxs:text-lg ${
                  !data.maxHighestReturn
                    ? ''
                    : Big(data.maxHighestReturn ?? 0).gte(0)
                    ? 'xxs:text-green-500'
                    : 'xxs:text-red-500'
                }`}
              >
                <>
                  <PercentageChangeWithMaxValue
                    className="font-bold"
                    change={data.maxHighestReturn || 0}
                    maxValue={999}
                  />
                </>
              </div>
            </div>
          </div>
          <div className="flex space-x-1 items-center">
            <div className="text-xs">🔼 {data.numberOfWins ?? '--'}</div>
            <div className="text-xs">🔽 {data.numberOfLosses ?? '--'}</div>
            <div className="text-xs">🚨 {data.numberOfRugPulls ?? '--'}</div>
          </div>
        </div>
      </div>
    ),
  },
];

export function PercentageChangeWithMaxValue({
  change,
  maxValue,
  ...rest
}: { change: BigSource } & {
  maxValue: number;
} & React.ComponentProps<'span'>) {
  const changeBig = Big(change);

  if (changeBig.eq(0)) return <span>--</span>;
  const changeBigCapped = changeBig.gt(maxValue) ? Big(maxValue) : changeBig;

  return (
    <span
      {...rest}
      className={twMerge(
        changeBigCapped.gt(0) ? 'text-green-success' : 'text-red-warning',
        rest.className
      )}
    >
      {changeBigCapped.gt(0) ? '+' : ''}
      {changeBigCapped.gte(1) ? (
        <>{changeBigCapped.plus(1).toFixed(1)}X</>
      ) : (
        <>
          {formatNumberWithSuffix(changeBigCapped.mul(100), { precision: 1 })}%
        </>
      )}
      {changeBigCapped.eq(Big(maxValue)) ? '+' : ''}
    </span>
  );
}
